import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Chip,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Language as LanguageIcon,
  CalendarToday as CalendarIcon,
  Lock as LockIcon,
  Class as ClassIcon,
} from "@mui/icons-material";
// import { selectCurrentUser } from "../../store/slices/authSlice";
import { useGetProfileQuery } from "@/store/api/authApi";
import ChangePasswordCard from "@/components/user/ChangePasswordCard";

const Profile = () => {
  // const user = useSelector(selectCurrentUser);
  const { data: profileData } = useGetProfileQuery();
  const { user } = profileData || {};

  const getRoleInfo = (role) => {
    const roles = {
      student: { label: "Student", color: "#2196f3", icon: SchoolIcon },
      teacher: { label: "Teacher", color: "#4caf50", icon: PersonIcon },
      admin: { label: "Administrator", color: "#ff5722", icon: LockIcon },
    };
    return roles[role] || roles.student;
  };

  const roleInfo = getRoleInfo(user?.role);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 2,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            mb: 4,
            textAlign: "center",
            p: 4,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          }}
        >
          {/* Header */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              background: "linear-gradient(45deg, #2196F3 30%, #1976D2 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            My Profile
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 4,
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                textAlign: "center",
                p: 3,
              }}
            >
              <Avatar
                src={user?.picture}
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  border: "4px solid white",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                }}
              >
                <PersonIcon sx={{ fontSize: 60 }} />
              </Avatar>

              <Typography variant="h5" fontWeight={600} gutterBottom>
                {user?.name || user?.firstName + " " + user?.lastName}
              </Typography>

              <Chip
                icon={React.createElement(roleInfo.icon)}
                label={roleInfo.label}
                sx={{
                  backgroundColor: roleInfo.color,
                  color: "white",
                  fontWeight: 500,
                  mb: 2,
                }}
              />
            </Card>
          </Grid>

          {/* Profile Information */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 4,
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight={600}>
                    Personal Information
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  {/* Name Field */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Name
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {user?.name || user?.firstName + " " + user?.lastName}
                    </Typography>
                  </Grid>

                  {/* Email Field */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Email
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {user?.email}
                    </Typography>
                  </Grid>

                  {/* Role Field */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <SchoolIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Role
                      </Typography>
                    </Box>
                    <Chip
                      icon={React.createElement(roleInfo.icon)}
                      label={roleInfo.label}
                      sx={{
                        backgroundColor: roleInfo.color,
                        color: "white",
                        fontWeight: 500,
                      }}
                    />
                  </Grid>

                  {/* Join Date */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Join Date
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {user?.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("vi-VN")
                        : "N/A"}
                    </Typography>
                  </Grid>

                  {/* Last Login */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Last Login
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {user?.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString("vi-VN")
                        : "N/A"}
                    </Typography>
                  </Grid>

                  {/* Class Information - Only for students */}
                  {user?.role === "student" && user?.class && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                          <ClassIcon sx={{ mr: 1 }} />
                          <Typography variant="h6" fontWeight={600}>
                            Class Information
                          </Typography>
                        </Box>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              Class Code
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {user.class.classCode || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              Class Name
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {user.class.className || "N/A"}
                            </Typography>
                          </Grid>
                          {user.class.description && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Description
                              </Typography>
                              <Typography variant="body1" fontWeight={500}>
                                {user.class.description}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Admins-only: change password */}
            {user?.role === "admin" && (
              <ChangePasswordCard />
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Profile;
